/**
 * UpgradeModal — Outcome-Based Paywall
 *
 * Shown when user hits a 402 subscription gate.
 * Copy: outcome-based ("unlock financing"), not feature-based ("get PDF").
 * Triggers Stripe checkout → redirects → returns with session_id → polls status.
 */
import React, { useState, useEffect } from "react";
import client from "@/api/client";
import { toast } from "sonner";
import { X, TrendingUp, Check, RefreshCw, Lock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const FEATURES = [
  "Generate a bank-ready Investment Memo PDF",
  "Submit directly to Financial Institutions",
  "Track financing progress and responses",
  "Digital Climate Credential (verifiable)",
  "Evidence-backed verification — all 4 levels",
];

export default function UpgradeModal({ onClose, frsScore, frsGrade }) {
  const { user } = useAuth();
  const [loading,   setLoading]   = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [polling,   setPolling]   = useState(false);
  const [upgraded,  setUpgraded]  = useState(false);

  // On return from Stripe — poll for payment status
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid    = params.get("session_id");
    const status = params.get("payment");
    if (sid && status === "success") {
      setSessionId(sid);
      setPolling(true);
      const poll = async (s, attempts = 0) => {
        if (attempts > 10) { setPolling(false); return; }
        try {
          const res = await client.get(`/payments/status/${s}`);
          if (res.data.payment_status === "paid") {
            setUpgraded(true); setPolling(false);
            toast.success("Pro subscription activated! All features unlocked.");
            window.history.replaceState({}, "", window.location.pathname);
            setTimeout(() => window.location.reload(), 1500);
          } else setTimeout(() => poll(s, attempts + 1), 2000);
        } catch { setTimeout(() => poll(s, attempts + 1), 3000); }
      };
      poll(sid);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const pollStatus = async (sid, attempts = 0) => {
    if (attempts > 10) { setPolling(false); return; }
    try {
      const res = await client.get(`/payments/status/${sid}`);
      if (res.data.payment_status === "paid") {
        setUpgraded(true);
        setPolling(false);
        toast.success("Pro subscription activated! All features unlocked.");
        // Clean URL
        window.history.replaceState({}, "", window.location.pathname);
        setTimeout(() => window.location.reload(), 1500);
      } else {
        setTimeout(() => pollStatus(sid, attempts + 1), 2000);
      }
    } catch {
      setTimeout(() => pollStatus(sid, attempts + 1), 3000);
    }
  };

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const origin = window.location.origin;
      const res    = await client.post("/payments/checkout", {
        origin_url: origin,
        package_id: "pro_monthly",
      });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout. Please try again.");
      setLoading(false);
    }
  };

  if (upgraded) return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-8 text-center">
        <div className="w-16 h-16 bg-emerald-50 rounded-xl flex items-center justify-center mx-auto mb-4">
          <Check size={28} className="text-[#059669]" />
        </div>
        <h3 className="text-lg font-bold text-[#0F172A] mb-2">Pro Access Activated</h3>
        <p className="text-sm text-slate-500">All features are now unlocked. Refreshing...</p>
      </div>
    </div>
  );

  if (polling) return (
    <div className="fixed inset-0 bg-black/60 z-[200] flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-md w-full p-8 text-center">
        <RefreshCw size={28} className="text-[#059669] mx-auto mb-3 animate-spin" />
        <p className="text-sm font-medium text-slate-700">Confirming your payment...</p>
        <p className="text-xs text-slate-400 mt-1">This takes a few seconds</p>
      </div>
    </div>
  );

  const score = frsScore || 0;
  const band  = score >= 70 ? "Investment Grade" : score >= 60 ? "close to funding" : score >= 50 ? "building readiness" : "early stage";

  return (
    <div className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4"
      data-testid="upgrade-modal">
      <div className="bg-white rounded-xl max-w-md w-full overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-[#0F172A] px-6 py-5">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-widest mb-1">DeCarbonIQ Pro</p>
              <h2 className="text-xl font-bold text-white leading-snug">
                Unlock Access to<br />Climate Financing
              </h2>
            </div>
            {onClose && (
              <button onClick={onClose} className="p-1.5 text-slate-500 hover:text-white mt-0.5">
                <X size={16} />
              </button>
            )}
          </div>
          {score > 0 && (
            <div className="mt-4 flex items-center gap-3 px-4 py-2.5 bg-white/10 rounded-xl">
              <TrendingUp size={15} className="text-[#059669]" />
              <div>
                <p className="text-xs text-white font-semibold">Your Financing Readiness: {score.toFixed(0)}/100 — {band}</p>
                <p className="text-[10px] text-slate-400 mt-0.5">
                  {score >= 60 ? "You're close. Unlock these tools to complete the journey." :
                   "Improve your score and access financing tools."}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="px-6 py-5">
          <p className="text-sm text-slate-700 font-medium mb-4">Upgrade to Pro to:</p>
          <div className="space-y-2.5 mb-5">
            {FEATURES.map((f, i) => (
              <div key={i} className="flex items-center gap-2.5">
                <div className="w-5 h-5 bg-emerald-50 rounded-full flex items-center justify-center flex-shrink-0">
                  <Check size={11} className="text-[#059669]" strokeWidth={3} />
                </div>
                <p className="text-sm text-[#0F172A]">{f}</p>
              </div>
            ))}
          </div>

          {/* Price */}
          <div className="bg-[#F8FAFC] rounded-xl p-4 border border-slate-200 mb-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-400 uppercase tracking-wider">Monthly Plan</p>
              <p className="text-2xl font-bold text-[#0F172A] mt-0.5">$49<span className="text-sm font-normal text-slate-400">/month</span></p>
            </div>
            <div className="text-right">
              <p className="text-xs text-slate-400">Cancel anytime</p>
              <p className="text-xs text-slate-400 mt-0.5">Powered by Stripe</p>
            </div>
          </div>

          <button onClick={handleCheckout} disabled={loading} data-testid="checkout-btn"
            className="w-full py-3 bg-[#059669] text-white text-sm font-bold rounded-xl hover:bg-emerald-700 disabled:opacity-60 transition-colors flex items-center justify-center gap-2">
            {loading
              ? <><RefreshCw size={14} className="animate-spin" /> Redirecting to Stripe...</>
              : "Start accessing funding opportunities →"}
          </button>
          {onClose && (
            <button onClick={onClose} className="w-full mt-2 py-2 text-xs text-slate-400 hover:text-slate-600">
              Maybe later
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
